import React, { forwardRef } from 'react';
import { motion } from 'framer-motion';
import { twMerge } from 'tailwind-merge';
import { Search, Mail, Lock, User } from 'lucide-react';

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  variant?: 'default' | 'search' | 'email' | 'password' | 'username';
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ 
    className, 
    label, 
    error, 
    leftIcon, 
    rightIcon, 
    variant = 'default',
    id,
    ...props 
  }, ref) => {
    const inputId = id || `input-${Math.random().toString(36).substr(2, 9)}`;

    const getDefaultIcon = () => {
      switch (variant) {
        case 'search':
          return <Search className="h-4 w-4 text-gray-400" />;
        case 'email':
          return <Mail className="h-4 w-4 text-gray-400" />;
        case 'password':
          return <Lock className="h-4 w-4 text-gray-400" />;
        case 'username':
          return <User className="h-4 w-4 text-gray-400" />;
        default:
          return null;
      }
    };

    const defaultIcon = getDefaultIcon();
    const icon = leftIcon || defaultIcon;

    return (
      <div className="space-y-2">
        {label && (
          <label 
            htmlFor={inputId} 
            className="block text-sm font-medium text-gray-700"
          >
            {label}
          </label>
        )}
        
        <div className="relative">
          {icon && (
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              {icon}
            </div>
          )}
          
                             <motion.input
                     ref={ref}
                     id={inputId}
                     className={twMerge(
                       'block w-full rounded-lg border border-gray-300 px-4 py-3 text-gray-900 placeholder-gray-500 transition-all duration-200',
                       'focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 focus:outline-none',
                       'disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed',
                       icon ? 'pl-10' : '',
                       rightIcon ? 'pr-10' : '',
                       error ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20' : '',
                       className
                     )}
                     whileFocus={{ scale: 1.01 }}
                     {...(props as any)}
                   />
          
          {rightIcon && (
            <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
              {rightIcon}
            </div>
          )}
        </div>
        
        {error && (
          <motion.p
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-sm text-red-600"
          >
            {error}
          </motion.p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';

export default Input;
